import { lazy, Suspense, useState } from "react";
import type { ComponentType } from "react";
import { Badge, Button, Dropdown, Grid, Layout, Menu, Result, Space, Tooltip, Typography } from "antd";
import type { MenuProps } from "antd";
import {
  Archive,
  Cable,
  FileSignature,
  FlaskConical,
  LayoutDashboard,
  ListChecks,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  PenTool,
  Rocket,
  ScrollText,
  Settings,
  ShieldCheck,
  UserRound,
  UserRoundCog,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "../app-context";
import { PageLoading } from "../components/PageLoadState";
import { BrandLogo } from "../components/BrandLogo";
import { SystemVersion } from "../components/SystemVersion";
import { canAccessRoute } from "../router";
import { LanguageMenu } from "../hooks/useLanguage";
import { ThemeToggle, useTheme } from "../hooks/useTheme";
import { useTableScrollRegions } from "../hooks/useTableScrollRegions";

const DashboardPage = lazy(() => import("../pages/Dashboard").then((module) => ({ default: module.DashboardPage })));
const TodosPage = lazy(() => import("../pages/Todos").then((module) => ({ default: module.TodosPage })));
const QuickStartPage = lazy(() => import("../pages/QuickStart").then((module) => ({ default: module.QuickStartPage })));
const DemoPage = lazy(() => import("../pages/Standards").then((module) => ({ default: module.DemoPage })));
const SettingsPage = lazy(() => import("../pages/Settings").then((module) => ({ default: module.SettingsPage })));
const AuditPage = lazy(() => import("../pages/Audit").then((module) => ({ default: module.AuditPage })));
const ArchivePage = lazy(() => import("../pages/Archive").then((module) => ({ default: module.ArchivePage })));
const DiagnosticsPage = lazy(() => import("../pages/Diagnostics").then((module) => ({ default: module.DiagnosticsPage })));
const MesApiDemoPage = lazy(() => import("../pages/MesApiDemo").then((module) => ({ default: module.MesApiDemoPage })));
const SignatureToolPage = lazy(() => import("../pages/SignatureTool").then((module) => ({ default: module.SignatureToolPage })));
const ManualIssuancePage = lazy(() => import("../pages/ManualIssuance").then((module) => ({ default: module.ManualIssuancePage })));
const IssuanceTasksPage = lazy(() => import("../pages/IssuanceTasks").then((module) => ({ default: module.IssuanceTasksPage })));
const IssuanceConfigurationsPage = lazy(() => import("../pages/IssuanceConfigurations").then((module) => ({ default: module.IssuanceConfigurationsPage })));
const IssuanceConfigurationDetailPage = lazy(() => import("../pages/IssuanceConfigurations").then((module) => ({ default: module.IssuanceConfigurationDetailPage })));
const IssuanceRevisionDetailPage = lazy(() => import("../pages/IssuanceRevisionDetail").then((module) => ({ default: module.IssuanceRevisionDetailPage })));

const { Header, Content, Sider, Footer } = Layout;

interface NavItem {
  nav: string;
  labelKey: string;
  icon: LucideIcon;
  role?: string;
}
interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "shell.overview",
    items: [
      { nav: "dashboard", labelKey: "shell.dashboard", icon: LayoutDashboard },
      { nav: "quick-start", labelKey: "shell.quickStart", icon: Rocket },
    ],
  },
  {
    labelKey: "shell.preparation",
    items: [{ nav: "issuance-configurations", labelKey: "shell.issuanceTemplates", icon: FileSignature }],
  },
  {
    labelKey: "shell.issuanceTraceability",
    items: [
      { nav: "issuance-tasks", labelKey: "shell.issuanceTasks", icon: ListChecks },
      { nav: "archive", labelKey: "shell.archives", icon: Archive },
    ],
  },
  {
    labelKey: "shell.tools",
    items: [
      { nav: "diagnostics", labelKey: "shell.diagnostics", icon: ShieldCheck },
      // 签名/验签与 IVI 校验同属工具链，紧邻展示；API 对接单独靠后
      { nav: "signature-tool", labelKey: "shell.signatureTool", icon: PenTool },
      { nav: "mes-api-demo", labelKey: "shell.mesApiDemo", icon: Cable },
      { nav: "demo", labelKey: "shell.demo", icon: FlaskConical },
    ],
  },
  {
    labelKey: "shell.systemManagement",
    items: [
      { nav: "settings", labelKey: "shell.settings", icon: Settings, role: "ADMIN" },
      { nav: "audit", labelKey: "shell.audit", icon: ScrollText, role: "ADMIN" },
    ],
  },
];

export const SIDEBAR_COLLAPSED_STORAGE_KEY = "vsign-sidebar-collapsed";

export function readSidebarCollapsed(): boolean {
  try {
    return globalThis.localStorage?.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function storeSidebarCollapsed(collapsed: boolean): void {
  try {
    globalThis.localStorage?.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // 浏览器存储不可用时，折叠状态仍在当前页面会话内生效。
  }
}

const PAGE_COMPONENTS: Record<string, ComponentType> = {
  dashboard: DashboardPage,
  todos: TodosPage,
  "quick-start": QuickStartPage,
  demo: DemoPage,
  settings: SettingsPage,
  "issuance-tasks": IssuanceTasksPage,
  audit: AuditPage,
  archive: ArchivePage,
  diagnostics: DiagnosticsPage,
  "mes-api-demo": MesApiDemoPage,
  "signature-tool": SignatureToolPage,
  "issuance-configurations": IssuanceConfigurationsPage,
};

function CurrentPage() {
  const { t } = useTranslation();
  const { route, user, navigate } = useApp();
  if (!route) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t("shell.notFound")}
        extra={<Button type="primary" onClick={() => navigate("/")}>{t("shell.returnDashboard")}</Button>}
      />
    );
  }
  if (!canAccessRoute(route, user?.role || "")) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("shell.forbidden")}
        extra={<Button type="primary" onClick={() => navigate("/")}>{t("shell.returnDashboard")}</Button>}
      />
    );
  }
  if (route.name === "issuance-revision-detail" && route.params.revisionId) {
    return (
      <IssuanceRevisionDetailPage
        configurationId={route.params.configurationId || ""}
        revisionId={route.params.revisionId}
      />
    );
  }
  if (route.name === "issuance-configuration-detail" && route.params.configurationId) {
    return <IssuanceConfigurationDetailPage configurationId={route.params.configurationId} />;
  }
  if (route.name === "issuance-task-manual-new") {
    return <ManualIssuancePage />;
  }
  if (route.name === "issuance-task-manual-edit" && route.params.taskId) {
    return <ManualIssuancePage taskId={route.params.taskId} />;
  }
  const Component = PAGE_COMPONENTS[route.view];
  return Component ? <Component /> : <Result status="404" title="404" subTitle={t("shell.notFound")} />;
}

export function ProductLogo({ environment }: { environment: string }) {
  const { t } = useTranslation();
  const isTest = environment === "TEST";

  return (
    <Badge
      className="product-logo-badge"
      count={isTest ? "TEST" : 0}
      size="small"
      title={isTest ? t("shell.testEnvironment") : undefined}
    >
      <span className="logo" role="img" aria-label={t("common.productName")}>
        <BrandLogo className="logo-wordmark" />
        <BrandLogo variant="mark" className="logo-mark" />
      </span>
    </Badge>
  );
}

export function ProductBrand({ environment }: { environment: string }) {
  const { t } = useTranslation();

  return (
    <div className="brand app-brand">
      <div className="brand-primary">
        <ProductLogo environment={environment} />
        <strong className="brand-product">eCoC</strong>
      </div>
      <small className="brand-subtitle">
        {t("common.softwareSubtitle")} · {t("common.softwareVersion")}
      </small>
    </div>
  );
}

export function AppShell() {
  const contentRef = useTableScrollRegions();
  const { t } = useTranslation();
  const { user, deploymentEnvironment, route, navigate, logout } = useApp();
  const { theme, toggle } = useTheme();
  const screens = Grid.useBreakpoint();
  const desktop = screens.lg !== false;
  const role = user?.role || "";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);

  const menuItems: MenuProps["items"] = NAV_GROUPS.flatMap((group) => {
    const children = group.items
      .filter((item) => !item.role || item.role === role)
      .map((item) => {
        const Icon = item.icon;
        return {
          key: item.nav,
          label: t(item.labelKey),
          icon: desktop ? <Icon aria-hidden /> : undefined,
        };
      });
    return children.length ? [{ type: "group" as const, label: t(group.labelKey), children }] : [];
  });

  const activeItem = NAV_GROUPS.flatMap((group) => group.items).find((item) => item.nav === route?.view);
  const pageTitle = activeItem ? t(activeItem.labelKey) : route?.moduleTitle || t("shell.dashboard");
  const roleName = role === "ADMIN"
    ? t("shell.roleAdmin")
    : role === "OPERATOR"
      ? t("shell.roleOperator")
      : t("shell.roleViewer");
  const userSummary = user ? `${user.displayName} · ${roleName}` : t("shell.userMenu");
  const accountMenuItems: MenuProps["items"] = [
    ...(user
      ? [{
          key: "identity",
          disabled: true,
          label: (
            <span className="account-menu-identity">
              <strong>{user.displayName}</strong>
              <small>{roleName}</small>
            </span>
          ),
        }]
      : []),
    ...(user ? [{ type: "divider" as const }] : []),
    { key: "browser-settings", icon: <Settings aria-hidden />, label: t("selectionCompatibility.title") },
    {
      key: "switch-role",
      icon: <UserRoundCog aria-hidden />,
      label: t("shell.switchRole"),
    },
    {
      key: "logout",
      icon: <LogOut aria-hidden />,
      label: t("shell.logout"),
      danger: true,
    },
  ];

  const navigation = (
    <Menu
      className="main-navigation"
      mode={desktop ? "inline" : "horizontal"}
      {...(desktop ? { inlineCollapsed: sidebarCollapsed } : {})}
      theme={desktop ? "dark" : theme}
      items={menuItems}
      selectedKeys={route?.view ? [route.view] : []}
      onClick={({ key }) => navigate(key)}
    />
  );

  return (
    <Layout className={`app-layout${desktop && sidebarCollapsed ? " sider-collapsed" : ""}`}>
      <Button
        className="skip-to-content"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >{t("common.skipToContent")}</Button>
      {desktop ? (
        <Sider
          className="app-sider"
          width={248}
          collapsedWidth={72}
          collapsed={sidebarCollapsed}
          trigger={null}
          theme="dark"
        >
          <ProductBrand environment={deploymentEnvironment} />
          <nav aria-label={t("common.productName")}>{navigation}</nav>
          <div className="sider-footer">
            <Tooltip title={sidebarCollapsed ? t("shell.expandNavigation") : t("shell.collapseNavigation")} placement="right">
              <Button
                type="text"
                className="sider-collapse-button"
                icon={sidebarCollapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
                aria-label={sidebarCollapsed ? t("shell.expandNavigation") : t("shell.collapseNavigation")}
                onClick={() => {
                  setSidebarCollapsed((current) => {
                    const next = !current;
                    storeSidebarCollapsed(next);
                    return next;
                  });
                }}
              />
            </Tooltip>
          </div>
        </Sider>
      ) : (
        <Header className="mobile-navigation">
          <ProductBrand environment={deploymentEnvironment} />
          <nav aria-label={t("common.productName")}>{navigation}</nav>
        </Header>
      )}
      <Layout className={desktop ? "app-main-layout has-sider" : "app-main-layout"}>
        <Header className="app-header">
          <div className="page-identity">
            <Typography.Title level={1} id="view-title">{pageTitle}</Typography.Title>
          </div>
          <Space className="header-actions" size={4}>
            <ThemeToggle theme={theme} onToggle={toggle} />
            <LanguageMenu />
            <Dropdown
              menu={{
                items: accountMenuItems,
                onClick: ({ key }) => {
                  if (key === "browser-settings") navigate("/settings/interface");
                  if (key === "switch-role" || key === "logout") void logout();
                },
              }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <Tooltip title={userSummary}>
                <Button
                  type="text"
                  className="app-utility-button"
                  icon={<UserRound aria-hidden />}
                  aria-label={t("shell.userMenu")}
                />
              </Tooltip>
            </Dropdown>
          </Space>
        </Header>
        <Content ref={contentRef} className="app-content" id="main-content" tabIndex={-1} aria-labelledby="view-title">
          <Suspense fallback={<PageLoading />}>
            <CurrentPage />
          </Suspense>
        </Content>
        <Footer className="system-version-footer"><SystemVersion /></Footer>
      </Layout>
    </Layout>
  );
}
